import type { OrientationParams, QuarterTurns } from '@/core/document/types'
import type { Rect, Size } from '@/core/geometry/types'
import { assertPositiveSize } from '@/core/geometry/validate'

export const MIN_STRAIGHTEN_DEG = -45
export const MAX_STRAIGHTEN_DEG = 45
export const STRAIGHTEN_STEP_DEG = 0.1
export const STRAIGHTEN_SNAP_DEG = 0.75

const HALF_TURN_DEGREES = 180
const QUARTER_TURN_DEGREES = 90
const STEP_PRECISION = 10
const GEOMETRY_EPSILON = 1e-7

export interface AffineMatrix {
  a: number
  b: number
  c: number
  d: number
  e: number
  f: number
}

export const IDENTITY_ORIENTATION: Readonly<OrientationParams> = {
  quarterTurns: 0,
  flipHorizontal: false,
  flipVertical: false,
  straightenDeg: 0,
}

export function assertOrientationParams(params: OrientationParams): void {
  if (!Number.isInteger(params.quarterTurns) || params.quarterTurns < 0 || params.quarterTurns > 3) {
    throw new Error('Orientation quarterTurns must be an integer from 0 through 3')
  }
  if (typeof params.flipHorizontal !== 'boolean' || typeof params.flipVertical !== 'boolean') {
    throw new Error('Orientation flip values must be booleans')
  }
  if (!Number.isFinite(params.straightenDeg)
    || params.straightenDeg < MIN_STRAIGHTEN_DEG
    || params.straightenDeg > MAX_STRAIGHTEN_DEG) {
    throw new Error(`Orientation straightenDeg must be between ${MIN_STRAIGHTEN_DEG} and ${MAX_STRAIGHTEN_DEG}`)
  }
  const scaled = params.straightenDeg * STEP_PRECISION
  if (Math.abs(scaled - Math.round(scaled)) > GEOMETRY_EPSILON) {
    throw new Error(`Orientation straightenDeg must use ${STRAIGHTEN_STEP_DEG} degree steps`)
  }
}

export function isIdentityOrientation(params: OrientationParams): boolean {
  return params.quarterTurns === 0
    && !params.flipHorizontal
    && !params.flipVertical
    && params.straightenDeg === 0
}

export function normalizedQuarterTurns(value: number): QuarterTurns {
  if (!Number.isInteger(value)) throw new Error('Quarter-turn normalization requires an integer')
  return ((value % 4) + 4) % 4 as QuarterTurns
}

export function orientedSize(source: Size, orientation?: OrientationParams): Size {
  assertPositiveSize(source, 'Orientation source')
  const params = orientation ?? IDENTITY_ORIENTATION
  assertOrientationParams(params)
  return params.quarterTurns % 2 === 0
    ? { width: source.width, height: source.height }
    : { width: source.height, height: source.width }
}

export function orientationMatrix(source: Size, orientation: OrientationParams): AffineMatrix {
  assertPositiveSize(source, 'Orientation source')
  assertOrientationParams(orientation)
  const output = orientedSize(source, orientation)
  const angle = (orientation.quarterTurns * QUARTER_TURN_DEGREES + orientation.straightenDeg)
    * Math.PI / HALF_TURN_DEGREES
  const cosine = Math.cos(angle)
  const sine = Math.sin(angle)
  const horizontalScale = orientation.flipHorizontal ? -1 : 1
  const verticalScale = orientation.flipVertical ? -1 : 1
  const a = horizontalScale * cosine
  const b = verticalScale * sine
  const c = horizontalScale * -sine
  const d = verticalScale * cosine
  const sourceCenterX = source.width / 2
  const sourceCenterY = source.height / 2

  return {
    a,
    b,
    c,
    d,
    e: output.width / 2 - a * sourceCenterX - c * sourceCenterY,
    f: output.height / 2 - b * sourceCenterX - d * sourceCenterY,
  }
}

function assertStraightenAngle(straightenDeg: number): void {
  if (!Number.isFinite(straightenDeg)
    || straightenDeg < MIN_STRAIGHTEN_DEG
    || straightenDeg > MAX_STRAIGHTEN_DEG) {
    throw new Error(`Straighten angle must be between ${MIN_STRAIGHTEN_DEG} and ${MAX_STRAIGHTEN_DEG}`)
  }
}

export function inscribedRect(size: Size, straightenDeg: number): Rect {
  assertPositiveSize(size, 'Straightened image')
  assertStraightenAngle(straightenDeg)
  if (straightenDeg === 0) return { x: 0, y: 0, width: size.width, height: size.height }

  const angle = Math.abs(straightenDeg) * Math.PI / HALF_TURN_DEGREES
  const cosine = Math.abs(Math.cos(angle))
  const sine = Math.abs(Math.sin(angle))
  const scale = Math.min(
    size.width / (cosine * size.width + sine * size.height),
    size.height / (sine * size.width + cosine * size.height),
  )
  const width = size.width * scale
  const height = size.height * scale
  return {
    x: (size.width - width) / 2,
    y: (size.height - height) / 2,
    width,
    height,
  }
}

export function orientationCropBounds(size: Size, orientation?: OrientationParams): Rect {
  const params = orientation ?? IDENTITY_ORIENTATION
  const bounds = orientedSize(size, params)
  return params.straightenDeg === 0
    ? { x: 0, y: 0, ...bounds }
    : inscribedRect(bounds, params.straightenDeg)
}

export function rectInsideRotatedImage(rect: Rect, size: Size, straightenDeg: number): boolean {
  assertPositiveSize(rect, 'Crop rectangle')
  assertPositiveSize(size, 'Straightened image')
  assertStraightenAngle(straightenDeg)
  const angle = straightenDeg * Math.PI / HALF_TURN_DEGREES
  const cosine = Math.cos(angle)
  const sine = Math.sin(angle)
  const centerX = size.width / 2
  const centerY = size.height / 2
  const halfWidth = size.width / 2 + GEOMETRY_EPSILON
  const halfHeight = size.height / 2 + GEOMETRY_EPSILON
  const corners = [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x, y: rect.y + rect.height },
    { x: rect.x + rect.width, y: rect.y + rect.height },
  ]

  return corners.every((corner) => {
    const offsetX = corner.x - centerX
    const offsetY = corner.y - centerY
    const sourceX = cosine * offsetX + sine * offsetY
    const sourceY = -sine * offsetX + cosine * offsetY
    return Math.abs(sourceX) <= halfWidth && Math.abs(sourceY) <= halfHeight
  })
}

export function rotateRect(rect: Rect, bounds: Size, direction: -1 | 1): Rect {
  assertPositiveSize(rect, 'Rotated rectangle')
  assertPositiveSize(bounds, 'Rotation bounds')
  if (direction !== -1 && direction !== 1) throw new Error('Rectangle rotation direction must be -1 or 1')
  return direction === 1
    ? {
        x: bounds.height - rect.y - rect.height,
        y: rect.x,
        width: rect.height,
        height: rect.width,
      }
    : {
        x: rect.y,
        y: bounds.width - rect.x - rect.width,
        width: rect.height,
        height: rect.width,
      }
}

function inverseMatrix(matrix: AffineMatrix): AffineMatrix {
  const determinant = matrix.a * matrix.d - matrix.b * matrix.c
  if (Math.abs(determinant) <= GEOMETRY_EPSILON) throw new Error('Orientation matrix is not invertible')
  return {
    a: matrix.d / determinant,
    b: -matrix.b / determinant,
    c: -matrix.c / determinant,
    d: matrix.a / determinant,
    e: (matrix.c * matrix.f - matrix.d * matrix.e) / determinant,
    f: (matrix.b * matrix.e - matrix.a * matrix.f) / determinant,
  }
}

function mapPoint(matrix: AffineMatrix, x: number, y: number): { x: number; y: number } {
  return {
    x: matrix.a * x + matrix.c * y + matrix.e,
    y: matrix.b * x + matrix.d * y + matrix.f,
  }
}

export function mapRectBetweenOrientations(
  rect: Rect,
  source: Size,
  from: OrientationParams,
  to: OrientationParams,
): Rect {
  assertPositiveSize(rect, 'Mapped orientation rectangle')
  const inverseFrom = inverseMatrix(orientationMatrix(source, from))
  const toMatrix = orientationMatrix(source, to)
  const corners = [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x, y: rect.y + rect.height },
    { x: rect.x + rect.width, y: rect.y + rect.height },
  ].map((point) => {
    const sourcePoint = mapPoint(inverseFrom, point.x, point.y)
    return mapPoint(toMatrix, sourcePoint.x, sourcePoint.y)
  })
  const left = Math.min(...corners.map((point) => point.x))
  const top = Math.min(...corners.map((point) => point.y))
  const right = Math.max(...corners.map((point) => point.x))
  const bottom = Math.max(...corners.map((point) => point.y))
  return {
    x: Math.round(left),
    y: Math.round(top),
    width: Math.round(right - left),
    height: Math.round(bottom - top),
  }
}
